/**
 * Registre de routes déclaratif — source unique pour le fil d'Ariane du
 * Topbar et la palette de commandes (⌘K). La sidebar garde sa structure
 * métier propre ; ce registre couvre l'ensemble des routes atteignables,
 * y compris celles sans entrée de menu (carrier-pool, ordering-parties…),
 * qui redeviennent ainsi accessibles.
 */
import type { LucideIcon } from 'lucide-react';
import {
  BarChart3,
  Building2,
  Calculator,
  ClipboardList,
  Database,
  FileSpreadsheet,
  Layers,
  LineChart,
  MessageSquareReply,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  Truck,
  Users,
} from 'lucide-react';
import type { FeatureKey } from '@carrierbridge/shared';

export interface RouteEntry {
  /** Motif react-router (segments `:param` supportés). */
  path: string;
  /** Clé i18n du libellé affiché (breadcrumb, palette). */
  labelKey: string;
  /** Chemin du parent pour le fil d'Ariane. */
  parent?: string;
  icon?: LucideIcon;
  /** Feature de tenant requise (RequireFeature). */
  feature?: FeatureKey;
  /** Rôles autorisés (RequireRole) ; absent = tous les rôles connectés. */
  roles?: string[];
  /** Proposée dans la palette ⌘K (les détails `:id` ne le sont pas). */
  navigable?: boolean;
  /** Clé i18n des mots-clés de recherche de la palette. */
  keywordsKey?: string;
}

const EDITORS = ['admin', 'editor'];

export const ROUTE_REGISTRY: RouteEntry[] = [
  { path: '/quote', labelKey: 'routes.quote', icon: Calculator, feature: 'quote', navigable: true, keywordsKey: 'routes.quote_keywords' },
  { path: '/quote-response', labelKey: 'routes.quote_response', icon: MessageSquareReply, feature: 'quoteResponse', roles: EDITORS, navigable: true, keywordsKey: 'routes.quote_response_keywords' },
  { path: '/quote-response/:id', labelKey: 'routes.quote_response.id', parent: '/quote-response', feature: 'quoteResponse', roles: EDITORS },
  { path: '/robert', labelKey: 'routes.robert', icon: Sparkles, feature: 'robert', navigable: true, keywordsKey: 'routes.robert_keywords' },

  { path: '/carriers', labelKey: 'routes.carriers', icon: Truck, roles: EDITORS, navigable: true, keywordsKey: 'routes.carriers_keywords' },
  { path: '/carriers/:id', labelKey: 'routes.carriers.id', parent: '/carriers' },
  { path: '/logistics-sites', labelKey: 'routes.logistics_sites', icon: Building2, roles: EDITORS, navigable: true, keywordsKey: 'routes.logistics_sites_keywords' },
  { path: '/logistics-sites/:id', labelKey: 'routes.logistics_sites.id', parent: '/logistics-sites' },

  { path: '/sourcing', labelKey: 'routes.sourcing', icon: Search, feature: 'sourcing', roles: EDITORS, navigable: true, keywordsKey: 'routes.sourcing_keywords' },
  { path: '/tenders', labelKey: 'routes.tenders', icon: ClipboardList, feature: 'tenders', roles: EDITORS, navigable: true, keywordsKey: 'routes.tenders_keywords' },
  { path: '/tenders/:id', labelKey: 'routes.tenders.id', parent: '/tenders' },
  { path: '/tenders/:tenderId/proposals/:proposalId', labelKey: 'routes.tenders.tenderId.proposals.proposalId', parent: '/tenders' },
  { path: '/auto-tenders/:id', labelKey: 'routes.auto_tenders.id', parent: '/tenders', feature: 'autoTenders', roles: EDITORS },
  { path: '/auto-reference', labelKey: 'routes.auto_reference', parent: '/tenders', icon: FileSpreadsheet, feature: 'autoTenders', roles: EDITORS, navigable: true },
  { path: '/carrier-pool', labelKey: 'routes.carrier_pool', icon: Users, feature: 'carrierPool', roles: EDITORS, navigable: true, keywordsKey: 'routes.carrier_pool_keywords' },
  { path: '/carrier-pool/:id', labelKey: 'routes.carrier_pool.id', parent: '/carrier-pool' },

  { path: '/operations', labelKey: 'routes.operations', icon: Layers, feature: 'operations', roles: EDITORS, navigable: true, keywordsKey: 'routes.operations_keywords' },
  { path: '/operations/referentiels', labelKey: 'routes.operations.referentiels', parent: '/operations', feature: 'operations', roles: EDITORS, navigable: true },
  { path: '/operations/transporteurs', labelKey: 'routes.operations.transporteurs', parent: '/operations', feature: 'operations', roles: EDITORS, navigable: true },

  { path: '/ordering-parties', labelKey: 'routes.ordering_parties', icon: Building2, roles: EDITORS, navigable: true, keywordsKey: 'routes.ordering_parties_keywords' },
  { path: '/analysis', labelKey: 'routes.analysis', icon: BarChart3, feature: 'budget', roles: EDITORS, navigable: true, keywordsKey: 'routes.analysis_keywords' },

  { path: '/data', labelKey: 'routes.data', icon: Database, roles: EDITORS, navigable: true, keywordsKey: 'routes.data_keywords' },
  { path: '/datasets', labelKey: 'routes.datasets', parent: '/data', roles: EDITORS, navigable: true, keywordsKey: 'routes.datasets_keywords' },
  { path: '/datasets/:id', labelKey: 'routes.datasets.id', parent: '/datasets' },
  { path: '/datasets/import/:id', labelKey: 'routes.datasets.import.id', parent: '/datasets' },
  { path: '/zones/new', labelKey: 'routes.zones.new', parent: '/data', roles: EDITORS },
  { path: '/zones/:id', labelKey: 'routes.zones.id', parent: '/data', roles: EDITORS },
  { path: '/pricing-grid-imports/:id', labelKey: 'routes.pricing_grid_imports.id', parent: '/data' },
  { path: '/imports', labelKey: 'routes.imports', parent: '/data', roles: EDITORS, navigable: true, keywordsKey: 'routes.imports_keywords' },
  { path: '/imports/:id', labelKey: 'routes.imports.id', parent: '/imports' },
  { path: '/imports/ancillary/new', labelKey: 'routes.imports.ancillary.new', parent: '/imports' },
  { path: '/ancillary-imports/:id', labelKey: 'routes.ancillary_imports.id', parent: '/imports' },

  { path: '/info/fuel-indices', labelKey: 'routes.info.fuel_indices', icon: LineChart, navigable: true, keywordsKey: 'routes.info.fuel_indices_keywords' },

  { path: '/settings', labelKey: 'routes.settings', icon: Settings, navigable: true },
  { path: '/settings/general', labelKey: 'routes.settings.general', parent: '/settings', roles: EDITORS, navigable: true },
  { path: '/settings/team', labelKey: 'routes.settings.team', parent: '/settings', roles: ['admin'], navigable: true, keywordsKey: 'routes.settings.team_keywords' },
  { path: '/settings/branding', labelKey: 'routes.settings.branding', parent: '/settings', roles: EDITORS, navigable: true, keywordsKey: 'routes.settings.branding_keywords' },
  { path: '/settings/emails', labelKey: 'routes.settings.emails', parent: '/settings', roles: EDITORS, navigable: true },
  { path: '/settings/activity', labelKey: 'routes.settings.activity', parent: '/settings', roles: EDITORS, navigable: true, keywordsKey: 'routes.settings.activity_keywords' },
  { path: '/settings/referential', labelKey: 'routes.settings.referential', parent: '/settings', roles: EDITORS, navigable: true, keywordsKey: 'routes.settings.referential_keywords' },
  { path: '/settings/compliance', labelKey: 'routes.settings.compliance', parent: '/settings', roles: EDITORS, navigable: true },
  { path: '/settings/compliance/:spaceId', labelKey: 'routes.settings.compliance.id', parent: '/settings/compliance', roles: EDITORS },
  { path: '/settings/grid-templates', labelKey: 'routes.settings.grid_templates', parent: '/settings', roles: EDITORS, navigable: true },
  { path: '/settings/zones', labelKey: 'routes.settings.zones', parent: '/settings', feature: 'autoTenders', roles: EDITORS, navigable: true },
  { path: '/settings/data', labelKey: 'routes.settings.data', parent: '/settings', roles: ['admin'], navigable: true, keywordsKey: 'routes.settings.data_keywords' },
  { path: '/settings/maturity', labelKey: 'routes.settings.maturity', parent: '/settings', roles: ['admin'], navigable: true },
  { path: '/settings/energy', labelKey: 'routes.settings.energy', parent: '/settings', navigable: true, keywordsKey: 'routes.settings.energy_keywords' },
  { path: '/settings/account', labelKey: 'routes.settings.account', parent: '/settings', navigable: true, keywordsKey: 'routes.settings.account_keywords' },

  { path: '/superadmin', labelKey: 'routes.superadmin', icon: ShieldCheck, roles: ['superadmin'], navigable: true },
];

/** Convertit un motif `/a/:id/b` en RegExp exacte. */
function patternToRegex(pattern: string): RegExp {
  const rx = pattern
    .split('/')
    .map((seg) => (seg.startsWith(':') ? '[^/]+' : seg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')))
    .join('/');
  return new RegExp(`^${rx}/?$`);
}

const COMPILED = ROUTE_REGISTRY.map((entry) => ({ entry, rx: patternToRegex(entry.path) }));

/** Route du registre correspondant à un pathname exact, sinon null. */
export function matchRoute(pathname: string): RouteEntry | null {
  const hit = COMPILED.find(({ rx }) => rx.test(pathname));
  return hit ? hit.entry : null;
}

/** Chaîne parent → enfant pour le fil d'Ariane (au plus 4 niveaux). */
export function buildBreadcrumbs(pathname: string): RouteEntry[] {
  const leaf = matchRoute(pathname);
  if (!leaf) return [];
  const chain: RouteEntry[] = [leaf];
  let cur = leaf;
  for (let i = 0; i < 4 && cur.parent; i++) {
    const parent = ROUTE_REGISTRY.find((r) => r.path === cur.parent);
    if (!parent) break;
    chain.unshift(parent);
    cur = parent;
  }
  return chain;
}

interface UserLike {
  role: string;
  moduleAccess?: Partial<Record<FeatureKey, boolean>> | null;
  company?: { features?: Partial<Record<FeatureKey, boolean>> } | null;
}

/**
 * Entrées proposables à la palette pour un utilisateur donné : filtrées par
 * rôle et features du tenant (mêmes règles que RequireRole/RequireFeature).
 */
export function navigableRoutes(
  user: UserLike,
  featureDefaults: Record<FeatureKey, boolean>,
): RouteEntry[] {
  return ROUTE_REGISTRY.filter((r) => {
    if (!r.navigable) return false;
    // Même règle que RequireRole : le superadmin n'est pas rattaché à une
    // société, les écrans métier lui répondent 403 — la palette ne doit pas
    // les lui proposer.
    if (r.roles && !r.roles.includes(user.role)) return false;
    if (r.feature && user.role !== 'superadmin') {
      const enabled = user.company?.features?.[r.feature] ?? featureDefaults[r.feature];
      if (!enabled) return false;
      // Hors admin, l'admin de la société peut avoir retiré le module à cette personne.
      if (user.role !== 'admin' && user.moduleAccess?.[r.feature] === false) return false;
    }
    if (r.path === '/superadmin' && user.role !== 'superadmin') return false;
    return true;
  });
}
